import React from 'react';
import {
    Building2,
    DollarSign,
    CheckCircle,
    Ruler,
    Map,
    AlertCircle,
    TrendingUp,
    Info,
    MapPin,
    Layers,
    Route,
    Calculator,
    Award,
    XCircle,
    AlertTriangle
} from 'lucide-react';
import { FSIV2Module } from '../../services/fsiV2';

// Module-specific icons and colors
const MODULE_CONFIG: Record<string, { icon: React.ReactNode; color: string; bgColor: string; title: string }> = {
    basic: {
        icon: <Building2 className="w-5 h-5" />,
        color: 'text-blue-600',
        bgColor: 'bg-gradient-to-r from-blue-500 to-cyan-500',
        title: 'Plot & FSI Analysis'
    },
    financial: {
        icon: <DollarSign className="w-5 h-5" />,
        color: 'text-emerald-600',
        bgColor: 'bg-gradient-to-r from-emerald-500 to-teal-500',
        title: 'Financial Analysis'
    },
    compliance: {
        icon: <CheckCircle className="w-5 h-5" />,
        color: 'text-purple-600',
        bgColor: 'bg-gradient-to-r from-purple-500 to-pink-500',
        title: 'Compliance Status'
    },
    height: {
        icon: <Ruler className="w-5 h-5" />,
        color: 'text-orange-600',
        bgColor: 'bg-gradient-to-r from-orange-500 to-red-500',
        title: 'Height Analysis'
    },
    margins: {
        icon: <Map className="w-5 h-5" />,
        color: 'text-indigo-600',
        bgColor: 'bg-gradient-to-r from-indigo-500 to-blue-500',
        title: 'Margin Requirements'
    },
    roads: {
        icon: <Route className="w-5 h-5" />,
        color: 'text-gray-600',
        bgColor: 'bg-gradient-to-r from-gray-500 to-slate-600',
        title: 'Road Analysis'
    },
    geometry: {
        icon: <MapPin className="w-5 h-5" />,
        color: 'text-teal-600',
        bgColor: 'bg-gradient-to-r from-teal-500 to-cyan-500',
        title: 'Geometry Data'
    }
};

interface ModuleResponseCardProps {
    module: FSIV2Module;
    data: any;
    timestamp?: string;
}

const ModuleResponseCard: React.FC<ModuleResponseCardProps> = ({ module, data, timestamp }) => {
    const config = MODULE_CONFIG[module] || {
        icon: <Info className="w-5 h-5" />,
        color: 'text-gray-600',
        bgColor: 'bg-gray-500',
        title: module.charAt(0).toUpperCase() + module.slice(1)
    };

    const formatNumber = (num: number) => new Intl.NumberFormat('en-IN').format(Math.round(num));
    const formatCurrency = (num: number) => {
        if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
        if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
        return `₹${formatNumber(num)}`;
    };
    const formatArea = (sqm: number) => `${formatNumber(sqm)} m² (${formatNumber(sqm * 10.764)} sqft)`;

    // Render basic module - matching actual API structure
    const renderBasicModule = () => {
        const { plot_info, zone_info, road_info, buildable_area, summary } = data;

        return (
            <div className="space-y-4">
                {/* Plot Info Header */}
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                        <MapPin className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900">{plot_info?.name || `Plot ${plot_info?.gid}`}</div>
                        <div className="text-sm text-gray-600">
                            FP No: {plot_info?.fp_no} • TPS: {plot_info?.tps_name || plot_info?.tps_no}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                            {plot_info?.location?.village && `${plot_info.location.village}, `}
                            {plot_info?.location?.district || 'Gujarat'}
                        </div>
                    </div>
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">Plot Area</div>
                        <div className="font-semibold text-gray-900">{formatNumber(plot_info?.area_sqm || 0)} m²</div>
                        <div className="text-xs text-gray-400">{formatNumber((plot_info?.area_sqm || 0) * 10.764)} sqft</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">Zone</div>
                        <div className="font-semibold text-gray-900">{zone_info?.zone_code}</div>
                        <div className="text-xs text-gray-400">{zone_info?.zone_name}</div>
                    </div>
                </div>

                {/* FSI Breakdown */}
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-4 border border-cyan-200">
                    <div className="flex items-center gap-2 mb-3">
                        <Calculator className="w-4 h-4 text-cyan-600" />
                        <h4 className="font-medium text-gray-800">FSI Breakdown</h4>
                    </div>

                    <div className="space-y-2">
                        {buildable_area?.fsi_breakdown && (
                            <>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Base FSI</span>
                                    <span className="font-medium">{buildable_area.fsi_breakdown.base_fsi}</span>
                                </div>
                                {buildable_area.fsi_breakdown.premium_fsi > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Premium FSI (Road)</span>
                                        <span className="font-medium text-emerald-600">+{buildable_area.fsi_breakdown.premium_fsi}</span>
                                    </div>
                                )}
                                {buildable_area.fsi_breakdown.chargeable_fsi > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Chargeable FSI</span>
                                        <span className="font-medium text-blue-600">+{buildable_area.fsi_breakdown.chargeable_fsi}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm pt-2 border-t border-cyan-200">
                                    <span className="font-medium text-gray-800">Effective FSI</span>
                                    <span className="font-bold text-cyan-700">{summary?.effective_fsi || buildable_area.fsi_breakdown.total_fsi}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Buildable Area */}
                <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                    <div className="flex items-center gap-2 mb-3">
                        <Layers className="w-4 h-4 text-emerald-600" />
                        <h4 className="font-medium text-gray-800">Buildable Area</h4>
                    </div>

                    <div className="text-2xl font-bold text-emerald-700 mb-2">
                        {formatNumber(summary?.total_buildable_sqm || buildable_area?.buildable_area?.total_buildable_sqm || 0)} m²
                    </div>
                    <div className="text-sm text-emerald-600">
                        {formatNumber((summary?.total_buildable_sqm || 0) * 10.764)} sqft total buildable
                    </div>

                    {buildable_area?.utilization && (
                        <div className="mt-3 pt-3 border-t border-emerald-200 grid grid-cols-3 gap-2 text-xs">
                            <div>
                                <div className="text-gray-500">Base</div>
                                <div className="font-medium">{buildable_area.utilization.base_percentage}%</div>
                            </div>
                            <div>
                                <div className="text-gray-500">Premium</div>
                                <div className="font-medium">{buildable_area.utilization.premium_percentage}%</div>
                            </div>
                            <div>
                                <div className="text-gray-500">Chargeable</div>
                                <div className="font-medium">{buildable_area.utilization.chargeable_percentage}%</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Road Info */}
                {road_info && (
                    <div className={`rounded-lg p-4 ${road_info.premium_applicable ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-2 mb-2">
                            <Route className="w-4 h-4 text-gray-600" />
                            <h4 className="font-medium text-gray-800">Road Access</h4>
                            {road_info.premium_applicable && (
                                <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <Award className="w-3 h-3" /> Premium
                                </span>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <div className="text-gray-500">Width</div>
                                <div className="font-semibold">{road_info.width_m}m</div>
                            </div>
                            <div>
                                <div className="text-gray-500">Band</div>
                                <div className="font-semibold">{road_info.band}</div>
                            </div>
                        </div>
                        {road_info.premium_applicable && (
                            <div className="mt-2 text-xs text-amber-700">
                                +{road_info.premium_percentage}% premium FSI ({road_info.premium_fsi} additional)
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Render financial module - matching actual API structure
    const renderFinancialModule = () => {
        const { charges_breakdown, total_charges, summary, notes, disclaimer } = data;

        return (
            <div className="space-y-4">
                {/* Total Charges Header */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 border border-emerald-200">
                    <div className="text-sm text-emerald-600 mb-1">Total Development Charges</div>
                    <div className="text-3xl font-bold text-emerald-700">
                        {formatCurrency(total_charges || summary?.total_charges || 0)}
                    </div>
                    <div className="text-sm text-emerald-600 mt-1">
                        ₹{formatNumber(summary?.cost_per_sqm || data.cost_per_sqm_buildable || 0)}/m² buildable
                    </div>
                </div>

                {/* Charges Breakdown */}
                {charges_breakdown && (
                    <div className="space-y-3">
                        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            Charges Breakdown
                        </h4>

                        {charges_breakdown.chargeable_fsi && (
                            <div className="bg-white rounded-lg p-3 border border-gray-200">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="font-medium text-gray-800">Chargeable FSI</div>
                                        <div className="text-xs text-gray-500">
                                            {formatNumber(charges_breakdown.chargeable_fsi.area_sqm)} m² @ ₹{formatNumber(charges_breakdown.chargeable_fsi.rate_per_sqm)}/m²
                                        </div>
                                    </div>
                                    <div className="font-semibold text-gray-900">
                                        {formatCurrency(charges_breakdown.chargeable_fsi.total_cost)}
                                    </div>
                                </div>
                            </div>
                        )}

                        {charges_breakdown.premium_fsi && (
                            <div className="bg-white rounded-lg p-3 border border-gray-200">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="font-medium text-gray-800">Premium FSI</div>
                                        <div className="text-xs text-gray-500">
                                            {formatNumber(charges_breakdown.premium_fsi.area_sqm)} m² @ ₹{formatNumber(charges_breakdown.premium_fsi.rate_per_sqm)}/m²
                                        </div>
                                    </div>
                                    <div className="font-semibold text-gray-900">
                                        {formatCurrency(charges_breakdown.premium_fsi.total_cost)}
                                    </div>
                                </div>
                            </div>
                        )}

                        {charges_breakdown.scrutiny_fees && (
                            <div className="bg-white rounded-lg p-3 border border-gray-200">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="font-medium text-gray-800">Scrutiny Fees</div>
                                        <div className="text-xs text-gray-500">
                                            {charges_breakdown.scrutiny_fees.percentage}% of Jantri value
                                        </div>
                                    </div>
                                    <div className="font-semibold text-gray-900">
                                        {formatCurrency(charges_breakdown.scrutiny_fees.total_cost)}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ROI Impact */}
                {summary?.estimated_roi_impact && (
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-blue-700">Estimated ROI Impact</span>
                            <span className="font-bold text-blue-700">{summary.estimated_roi_impact}%</span>
                        </div>
                    </div>
                )}

                {/* Notes */}
                {notes && notes.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                        <h4 className="text-xs font-medium text-gray-600 mb-2">Notes</h4>
                        <ul className="text-xs text-gray-500 space-y-1">
                            {notes.slice(0, 3).map((note: string, i: number) => (
                                <li key={i}>• {note}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Disclaimer */}
                {disclaimer && (
                    <div className="text-xs text-gray-400 italic">{disclaimer}</div>
                )}
            </div>
        );
    };

    // Render compliance module - matching actual API structure
    const renderComplianceModule = () => {
        const { compliant, summary: complianceSummary, issues, warnings, plot_details, road_data_available, road_width_adequate } = data;

        return (
            <div className="space-y-4">
                {/* Compliance Status Header */}
                <div className={`rounded-xl p-4 ${compliant ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-full ${compliant ? 'bg-emerald-100' : 'bg-red-100'}`}>
                            {compliant ? (
                                <CheckCircle className="w-8 h-8 text-emerald-600" />
                            ) : (
                                <XCircle className="w-8 h-8 text-red-600" />
                            )}
                        </div>
                        <div>
                            <div className={`text-xl font-bold ${compliant ? 'text-emerald-700' : 'text-red-700'}`}>
                                {complianceSummary || (compliant ? 'Compliant' : 'Non-Compliant')}
                            </div>
                            <div className={`text-sm ${compliant ? 'text-emerald-600' : 'text-red-600'}`}>
                                {compliant ? 'Meets all requirements' : 'Issues found'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Plot Details */}
                {plot_details && (
                    <div className="bg-gray-50 rounded-lg p-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Plot Details</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                                <span className="text-gray-500">Area:</span>
                                <span className="ml-1 font-medium">{formatNumber(plot_details.area_sqm)} m²</span>
                            </div>
                            <div>
                                <span className="text-gray-500">Zone:</span>
                                <span className="ml-1 font-medium">{plot_details.zone_code}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">Use:</span>
                                <span className="ml-1 font-medium">{plot_details.proposed_use}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">Road:</span>
                                <span className="ml-1 font-medium">{plot_details.road_width_m}m</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Status Checks */}
                <div className="space-y-2">
                    <div className={`flex items-center gap-2 p-2 rounded-lg ${road_data_available ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                        {road_data_available ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                        ) : (
                            <AlertCircle className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="text-sm text-gray-700">Road data available</span>
                    </div>
                    <div className={`flex items-center gap-2 p-2 rounded-lg ${road_width_adequate ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                        {road_width_adequate ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                        ) : (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                        )}
                        <span className="text-sm text-gray-700">Road width adequate</span>
                    </div>
                </div>

                {/* Issues */}
                {issues && issues.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                            <XCircle className="w-4 h-4" />
                            Issues
                        </h4>
                        <ul className="text-sm text-red-600 space-y-1">
                            {issues.map((issue: string, i: number) => (
                                <li key={i}>• {issue}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Warnings */}
                {warnings && warnings.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <h4 className="text-sm font-medium text-amber-700 mb-2 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            Warnings
                        </h4>
                        <ul className="text-sm text-amber-600 space-y-1">
                            {warnings.map((warning: string, i: number) => (
                                <li key={i}>• {warning}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    // Render height module
    const renderHeightModule = () => {
        const { max_height_m, permissible_floors, floor_height_m, factors, summary: heightSummary } = data;

        return (
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                        <div className="text-xs text-orange-600 mb-1">Max Height</div>
                        <div className="text-2xl font-bold text-orange-700">{max_height_m || 0}m</div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                        <div className="text-xs text-orange-600 mb-1">Permissible Floors</div>
                        <div className="text-2xl font-bold text-orange-700">G+{(permissible_floors || 1) - 1}</div>
                        <div className="text-xs text-orange-500">{floor_height_m || 3}m per floor</div>
                    </div>
                </div>

                {factors && factors.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Height Factors</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                            {factors.map((factor: string, i: number) => (
                                <li key={i}>• {factor}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {heightSummary && (
                    <div className="text-sm text-gray-500">{heightSummary}</div>
                )}
            </div>
        );
    };

    // Render margins module - showing setbacks/margins matching API structure
    const renderMarginsModule = () => {
        const {
            side_margin_m,
            rear_margin_m,
            applicable_rule,
            impact_analysis,
            building_height_m,
            plot_area_sqm,
            notes,
            summary: marginSummary
        } = data;

        // Get front margin from height restrictions if available (from height module context)
        const frontMargin = applicable_rule?.margin_front || 9.0; // Default front margin
        const sideMargin = side_margin_m || applicable_rule?.margin_side || 0;
        const rearMargin = rear_margin_m || applicable_rule?.margin_rear || 0;

        return (
            <div className="space-y-4">
                {/* Summary Header */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-200">
                    <div className="text-center mb-3">
                        <h4 className="text-lg font-semibold text-indigo-800">Plot Margin Requirements</h4>
                        {marginSummary && (
                            <p className="text-sm text-indigo-600 mt-1">{marginSummary}</p>
                        )}
                    </div>

                    {/* Visual Plot Layout */}
                    <div className="relative w-full max-w-[300px] mx-auto py-4">
                        {/* Road/Front label */}
                        <div className="text-center mb-3">
                            <div className="text-xs text-gray-500 mb-1">ROAD SIDE</div>
                            <span className="inline-flex items-center gap-1 bg-indigo-500 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-md">
                                ↑ {frontMargin}m Front
                            </span>
                        </div>

                        <div className="flex items-center justify-center gap-4">
                            {/* Left Side margin */}
                            <div className="flex flex-col items-center">
                                <span className="bg-blue-500 text-white px-2 py-3 rounded-lg text-xs font-bold shadow-md writing-vertical" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
                                    {sideMargin}m
                                </span>
                                <div className="text-xs text-gray-400 mt-1">SIDE</div>
                            </div>

                            {/* Plot box */}
                            <div className="relative">
                                <div className="w-36 h-28 bg-gradient-to-br from-emerald-100 to-emerald-200 border-4 border-emerald-500 rounded-xl shadow-lg flex flex-col items-center justify-center">
                                    <div className="text-emerald-700 font-bold text-base">BUILDABLE</div>
                                    <div className="text-emerald-600 text-xs font-medium">AREA</div>
                                    {impact_analysis?.buildable_footprint_sqm && (
                                        <div className="text-emerald-800 text-sm font-bold mt-1">
                                            {formatNumber(impact_analysis.buildable_footprint_sqm)} m²
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Side margin */}
                            <div className="flex flex-col items-center">
                                <span className="bg-purple-500 text-white px-2 py-3 rounded-lg text-xs font-bold shadow-md" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
                                    {sideMargin}m
                                </span>
                                <div className="text-xs text-gray-400 mt-1">SIDE</div>
                            </div>
                        </div>

                        {/* Rear margin */}
                        <div className="text-center mt-3">
                            <span className="inline-flex items-center gap-1 bg-amber-500 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-md">
                                ↓ {rearMargin}m Rear
                            </span>
                            <div className="text-xs text-gray-500 mt-1">BACK SIDE</div>
                        </div>
                    </div>
                </div>

                {/* Margin Details Cards */}
                <div className="grid grid-cols-3 gap-2">
                    <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-200 text-center">
                        <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-2">
                            <span className="text-white text-xs font-bold">F</span>
                        </div>
                        <div className="text-2xl font-bold text-indigo-700">{frontMargin}m</div>
                        <div className="text-xs text-indigo-600 font-medium">FRONT</div>
                        <div className="text-xs text-indigo-400">Road setback</div>
                    </div>

                    <div className="bg-blue-50 rounded-xl p-3 border border-blue-200 text-center">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                            <span className="text-white text-xs font-bold">S</span>
                        </div>
                        <div className="text-2xl font-bold text-blue-700">{sideMargin}m</div>
                        <div className="text-xs text-blue-600 font-medium">SIDES</div>
                        <div className="text-xs text-blue-400">Both sides</div>
                    </div>

                    <div className="bg-amber-50 rounded-xl p-3 border border-amber-200 text-center">
                        <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-2">
                            <span className="text-white text-xs font-bold">R</span>
                        </div>
                        <div className="text-2xl font-bold text-amber-700">{rearMargin}m</div>
                        <div className="text-xs text-amber-600 font-medium">REAR</div>
                        <div className="text-xs text-amber-400">Back setback</div>
                    </div>
                </div>

                {/* Impact Analysis */}
                {impact_analysis && (
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Calculator className="w-4 h-4" />
                            Impact Analysis
                        </h5>
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <div>
                                <div className="text-xs text-gray-500">Original Plot</div>
                                <div className="font-bold text-gray-800">{formatNumber(impact_analysis.original_plot_area_sqm)} m²</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">Buildable</div>
                                <div className="font-bold text-emerald-600">{formatNumber(impact_analysis.buildable_footprint_sqm)} m²</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">Margin Impact</div>
                                <div className="font-bold text-red-600">-{impact_analysis.margin_impact_pct}%</div>
                            </div>
                        </div>

                        {/* Visual progress bar */}
                        <div className="mt-3">
                            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full"
                                    style={{ width: `${100 - (impact_analysis.margin_impact_pct || 0)}%` }}
                                ></div>
                            </div>
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                                <span>Buildable: {(100 - (impact_analysis.margin_impact_pct || 0)).toFixed(1)}%</span>
                                <span>Margins: {impact_analysis.margin_impact_pct}%</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Applicable Rule Info */}
                {applicable_rule && (
                    <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100">
                        <h5 className="text-xs font-semibold text-blue-700 mb-2">Applicable Rule</h5>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-gray-500">Height Range:</span>
                                <span className="font-medium text-gray-700">{applicable_rule.height_min}m - {applicable_rule.height_max}m</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Min Plot Size:</span>
                                <span className="font-medium text-gray-700">{formatNumber(applicable_rule.plot_size_min)} m²</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Building Height Context */}
                {building_height_m && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                        <Ruler className="w-4 h-4 text-gray-400" />
                        <span>Margins calculated for building height of <strong>{building_height_m}m</strong></span>
                    </div>
                )}

                {/* Notes */}
                {notes && notes.length > 0 && (
                    <div className="space-y-1">
                        {notes.map((note: string, i: number) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                                <span>{note}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Render roads module - showing primary and nearby roads
    const renderRoadsModule = () => {
        const { primary_road, nearby_roads, roads_found_count, tps_id, search_method, summary: roadSummary, status } = data;

        return (
            <div className="space-y-4">
                {/* Summary */}
                {roadSummary && (
                    <div className="bg-gradient-to-r from-slate-50 to-gray-100 rounded-xl p-4 border border-slate-200">
                        <div className="flex items-center gap-2">
                            <Route className="w-5 h-5 text-slate-600" />
                            <p className="text-slate-700 font-medium">{roadSummary}</p>
                        </div>
                        {tps_id && (
                            <div className="mt-2 text-xs text-slate-500">TPS: {tps_id}</div>
                        )}
                    </div>
                )}

                {/* Primary Road */}
                {primary_road && (
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                        <h5 className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                            <Award className="w-4 h-4" />
                            Primary Road (Abutting)
                        </h5>
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-2xl font-bold text-emerald-800">{primary_road.width_m}m</div>
                                <div className="text-sm text-emerald-600">{primary_road.name || `Road ${primary_road.gid}`}</div>
                                <div className="text-xs text-emerald-500">{primary_road.road_type}</div>
                            </div>
                            {primary_road.premium?.applicable && (
                                <div className="bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl px-4 py-3 text-center shadow-lg">
                                    <div className="text-white text-xs font-medium">{primary_road.premium.band}</div>
                                    <div className="text-white text-2xl font-bold">+{primary_road.premium.premium_percentage}%</div>
                                    <div className="text-amber-100 text-xs">FSI Premium</div>
                                </div>
                            )}
                        </div>
                        {primary_road.premium?.description && (
                            <div className="mt-3 text-sm text-emerald-600 bg-emerald-100 rounded-lg px-3 py-2">
                                {primary_road.premium.description}
                            </div>
                        )}
                    </div>
                )}

                {/* Nearby Roads */}
                {nearby_roads && nearby_roads.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Route className="w-4 h-4" />
                            Nearby Roads ({roads_found_count || nearby_roads.length} found)
                        </h5>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {nearby_roads.map((road: any, i: number) => (
                                <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                                            {i + 1}
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-gray-800">{road.name || `Road ${road.gid}`}</div>
                                            <div className="text-xs text-gray-500">{road.road_type}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-bold text-gray-800">{road.width_m}m</div>
                                        {road.distance_m !== null && (
                                            <div className="text-xs text-gray-400">{road.distance_m}m away</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Search Method Info */}
                {search_method && (
                    <div className="text-xs text-gray-400 text-center">
                        Data source: {search_method}
                    </div>
                )}
            </div>
        );
    };

    // Render generic module data
    const renderGenericModule = () => {
        return (
            <div className="bg-gray-50 rounded-lg p-3">
                <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(data, null, 2)}
                </pre>
            </div>
        );
    };

    // Select renderer based on module
    const renderContent = () => {
        switch (module) {
            case 'basic':
                return renderBasicModule();
            case 'financial':
                return renderFinancialModule();
            case 'compliance':
                return renderComplianceModule();
            case 'height':
                return renderHeightModule();
            case 'margins':
                return renderMarginsModule();
            case 'roads':
                return renderRoadsModule();
            default:
                return renderGenericModule();
        }
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            {/* Header */}
            <div className={`${config.bgColor} px-4 py-3 flex items-center gap-2`}>
                <div className="text-white">{config.icon}</div>
                <h3 className="text-white font-medium">{config.title}</h3>
            </div>

            {/* Content */}
            <div className="p-4">
                {renderContent()}
            </div>

            {/* Footer with timestamp */}
            {timestamp && (
                <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
                    Updated {new Date(timestamp).toLocaleTimeString()}
                </div>
            )}
        </div>
    );
};

export default ModuleResponseCard;
